import React, { useCallback } from 'react'
import styles from '../index.module.scss'
import { Spinner } from '../../../pages/shared/Spinner'
import QUERY_KEYS from '../../../graphql/queryKeys'
import { ArrayParam, NumericArrayParam, StringParam, useQueryParams } from 'use-query-params'
import useQuery from '../../../hooks/useQuery'
import { captureErrorAndNotify } from '../../../utilities/errorHandlers'
import { useSelector } from 'react-redux'
import Heatmap from './Heatmap'
import reportsTicketHeatmapQuery from '../../../graphql/queries/reportsTicketHeatmap'
import reportsFiltersQuery from '../../../graphql/queries/reportsFilters'
import ReportsFilterForm from '../reports-filter-form'

function TicketHeatmap () {
  const { hauler } = useSelector(({ user: { user, hauler } }) => ({ user, hauler }))
  const [queryParams, setQueryParams] = useQueryParams({
    startDate: StringParam,
    endDate: StringParam,
    years: NumericArrayParam,
    resourceTypes: ArrayParam,
    ticketTypes: ArrayParam,
    drivers: ArrayParam,
    status: ArrayParam
  })
  // Curried function to make sure we are always using 'replace' update.
  const setQueryParamsReplace = useCallback((params) => {
    setQueryParams(params, 'replace')
  }, [setQueryParams])

  const { data: reportData, isFetching } = useQuery([
    QUERY_KEYS.reportsTicketHeatmap,
    queryParams.startDate || null,
    queryParams.endDate || null,
    queryParams.years?.length > 0 ? queryParams.years?.map((e) => Number(e)) : null,
    queryParams.resourceTypes?.length > 0 ? queryParams.resourceTypes : null,
    queryParams.ticketTypes?.length > 0 ? queryParams.ticketTypes : null,
    queryParams.drivers?.length > 0 ? queryParams.drivers : null,
    queryParams.status?.length > 0 ? queryParams.status : null
  ],
  reportsTicketHeatmapQuery,
  {
    initialData: { reportsTicketHeatmap: [] },
    onError: (error) => {
      captureErrorAndNotify(error, 'Failed to fetch reports data')
    }
  })

  const { data: { reportsFilters = [] } = {}, isFetching: isFetchingReportsFilters } = useQuery(
    [QUERY_KEYS.reportsFilters],
    reportsFiltersQuery,
    {
      onError (error) {
        captureErrorAndNotify(error, 'Failed to retrieve resource types')
      },
      placplaceholderData: {
        reportFilters: {
          resources: [], ticketTypes: [], drivers: []
        }
      }
    }
  )

  return (
    <div className={styles.reportsDiv}>
      <div>
        <ReportsFilterForm
          handleInputChange={setQueryParamsReplace}
          showOptions={['dateRange', 'years', 'resourceTypes', 'ticketTypes', 'status', 'drivers']}
          options={{
            resourceTypes: reportsFilters?.resources?.map(opt => ({
              label: `${opt.name} (${opt.shortCode})`,
              value: opt.id
            })) || [],
            ticketTypes: reportsFilters?.ticketTypes?.map(opt => ({
              label: `${opt.name} (${opt.shortCode})`,
              value: opt.id
            })) || [],
            drivers: reportsFilters?.drivers?.map(opt => ({
              label: `${opt.name}`,
              value: opt.id
            })) || []
          }}
          initialValues={queryParams}
          clearSearch={() => setQueryParamsReplace({
            startDate: undefined,
            endDate: undefined,
            years: undefined,
            resourceTypes: undefined,
            ticketTypes: undefined,
            drivers: undefined,
            status: undefined
          })}
        />
      </div>
      <div className={styles.reportCharts}>
        <Spinner isFetching={reportData === undefined || isFetching || isFetchingReportsFilters}>
          <Heatmap
            heatMapData={reportData?.reportsTicketHeatmap?.map((item) => ({ lat: item.latitude, lng: item.longitude, count: item.totalTickets })) || []}
            hauler={{ lat: hauler.latitude, lng: hauler.longitude }}
          />
        </Spinner>
      </div>
    </div>
  )
}

export default TicketHeatmap
